import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { listScheduleRuns, getScheduleRun } from '../utils/api';
import { PageHeader, Panel, StatusPill, Empty, Skeleton, Icon, fmtDate, Pager, usePager } from '../components/ui';
import { NOTES_APP_URL } from '../components/Sidebar';

const DEFAULT_LIMIT = 26;
const MAX_LIMIT = 100;

// Runs are started from the notes app; this page only reads the log.
const RERUN_URL = `${NOTES_APP_URL}/admin?section=ai-agent`;

const SKIP_REASONS = {
  note_exists: 'Note already existed',
  client_missing: 'Client record not found',
  no_assessment: 'No functional assessment on file',
};

const ALERT_LABELS = {
  ses: 'Failure email sent',
  sns: 'Failure report sent via the alarm topic',
  failed: 'Failure email could not be sent',
};

// The kit's fmtDate only handles YYYY-MM-DD; runs carry full timestamps.
function fmtDateTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function fmtDuration(ms) {
  if (ms == null) return '';
  const s = Math.round(ms / 1000);
  return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${s % 60}s`;
}

// 'scheduled' is the Friday EventBridge run, 'manual' is Generate Now on the Auto
// Schedule tab, 'batch' is Batch Generate on the AI Live-in Caregiver Notes page.
const TRIGGER_LABELS = { scheduled: 'Scheduled', manual: 'Manual', batch: 'Batch' };
const triggerLabel = (run) => TRIGGER_LABELS[run.trigger] || 'Scheduled';
const triggeredBy = (run) => (run.trigger !== 'scheduled' && run.triggeredBy ? <div className="ops-cell-sub">by {run.triggeredBy}</div> : null);
// Batch runs can cover several weeks; weekBegin is the earliest.
const weeksNote = (run) => (run.weeks > 1 ? <div className="ops-cell-sub">{run.weeks} weeks, earliest shown</div> : null);

// A network-level failure surfaces as "Failed to fetch", which means nothing to staff.
const loadError = (e, fallback) => (!e?.message || /failed to fetch/i.test(e.message)
  ? `${fallback}. Check your connection and try again.`
  : e.message);

function resultDetail(r) {
  if (r.outcome === 'generated') return <span className="mono">{r.noteId || ''}</span>;
  if (r.outcome === 'skipped') return SKIP_REASONS[r.reason] || r.reason || '';
  return <span className="neg">{r.error || 'Unknown error'}</span>;
}

export default function ScheduleRunsPage() {
  const [params, setParams] = useSearchParams();
  const openId = params.get('run') || '';
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [tick, setTick] = useState(0);
  const [rows, setRows] = useState(null);
  const [error, setError] = useState('');
  const [detail, setDetail] = useState(null);
  const [detailError, setDetailError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { runs = [] } = await listScheduleRuns(limit);
        if (!cancelled) { setRows(runs); setError(''); }
      } catch (e) { if (!cancelled) { setError(loadError(e, 'Could not load the run log')); setRows([]); } }
    })();
    return () => { cancelled = true; };
  }, [limit, tick]);

  const { pageRows, pagerProps } = usePager(rows || [], [limit]);

  const setParam = (k, v) => { const p = new URLSearchParams(params); if (v) p.set(k, v); else p.delete(k); setParams(p, { replace: true }); };
  // The detail drawer is keyed by ?run=ID so a link can open it directly.
  const selected = useMemo(() => (rows || []).find(r => r.runId === openId) || null, [rows, openId]);
  const openRun = (r) => setParam('run', r.runId);
  const closeRun = () => setParam('run', '');
  useEffect(() => {
    if (!openId) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setParams(p => { const n = new URLSearchParams(p); n.delete('run'); return n; }, { replace: true }); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [openId, setParams]);

  useEffect(() => {
    if (!openId) { setDetail(null); setDetailError(''); return undefined; }
    let cancelled = false;
    setDetail(null); setDetailError('');
    getScheduleRun(openId)
      .then(({ run }) => { if (!cancelled) setDetail(run); })
      .catch(e => { if (!cancelled) setDetailError(loadError(e, 'Could not load this run')); });
    return () => { cancelled = true; };
  }, [openId]);

  const head = detail || selected;
  const canShowMore = rows && rows.length >= limit && limit < MAX_LIMIT;

  return (
    <main className="main-content ops-main">
      <div className="ops-page">
        <PageHeader title="Auto-Schedule Log"
          subtitle="Every AI Live-in Caregiver Notes run: the Friday schedule, Generate Now and Batch Generate. Click a row to see which clients were generated, skipped or failed."
          actions={<>
            <button type="button" className="ops-btn" onClick={() => setTick(t => t + 1)}><Icon name="refresh" /> Refresh</button>
            <a href={RERUN_URL} target="_blank" rel="noreferrer" className="ops-btn ops-btn--primary"><Icon name="external" /> Open Auto-Schedule</a>
          </>} />
        {error && <div className="ops-error">{error}</div>}
        <Panel flush title="Runs" count={rows ? rows.length : undefined}
          action={canShowMore ? <button type="button" className="ops-btn ops-btn--sm" onClick={() => setLimit(MAX_LIMIT)}>Show older runs</button> : null}
          foot={rows && rows.length > 0 ? <Pager {...pagerProps} label="runs" /> : null}>
          {!rows ? <Skeleton rows={6} /> : rows.length === 0 ? <Empty title="No runs yet">The first entry appears after the next Friday run, or after Generate Now or Batch Generate is used in the notes app.</Empty> : (
            <div className="ops-table-wrap">
              <table className="ops-table">
                <thead><tr>
                  <th>Week of</th><th>Ran</th><th>Trigger</th>
                  <th className="num">Generated</th><th className="num">Skipped</th><th className="num">Failed</th>
                  <th>Status</th><th></th>
                </tr></thead>
                <tbody>
                  {pageRows.map(r => (
                    <tr key={r.runId} className={`is-clickable${openId === r.runId ? ' is-selected' : ''}`} onClick={() => openRun(r)}>
                      <td><div className="ops-cell-main">{fmtDate(r.weekBegin)}</div>{weeksNote(r)}</td>
                      <td><div className="ops-cell-main">{fmtDateTime(r.startedAt)}</div>{r.durationMs != null && <div className="ops-cell-sub">{fmtDuration(r.durationMs)}</div>}</td>
                      <td>{triggerLabel(r)}{triggeredBy(r)}</td>
                      <td className="num">{r.generated}</td>
                      <td className="num">{r.skipped}</td>
                      <td className={`num${r.errors > 0 ? ' neg' : ''}`}>{r.errors}</td>
                      <td><StatusPill status={r.status} /></td>
                      <td onClick={e => e.stopPropagation()}>
                        <button type="button" className="ops-btn ops-btn--sm" onClick={() => openRun(r)}>Open</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      </div>
      {openId && (
        <>
          <div className="ops-drawer__backdrop" onClick={closeRun} />
          <aside className="ops-drawer" role="dialog" aria-modal="true" aria-label="Auto-schedule run">
            <div className="ops-drawer__head">
              <div>
                <div className="ops-label">Auto-schedule run</div>
                <h2 style={{ margin: '4px 0 2px' }}>{head ? (head.weeks > 1 ? `${head.weeks} weeks from ${fmtDate(head.weekBegin)}` : `Week of ${fmtDate(head.weekBegin)}`) : 'Run'}</h2>
                <div className="ops-cell-sub">{head ? `${fmtDateTime(head.startedAt)} · ${triggerLabel(head)}` : ''}</div>
              </div>
              <button type="button" className="ops-close" onClick={closeRun} aria-label="Close"><Icon name="x" /></button>
            </div>
            <div className="ops-drawer__body">
              {detailError && <div className="ops-error">{detailError}</div>}
              {!detail && !detailError && <Skeleton rows={5} />}
              {detail && (
                <>
                  <dl className="ops-dl">
                    <div><dt>Status</dt><dd><StatusPill status={detail.status} /></dd></div>
                    <div><dt>Trigger</dt><dd>{triggerLabel(detail)}{triggeredBy(detail)}</dd></div>
                    <div><dt>{detail.trigger === 'batch' ? 'Requested' : 'Enrolled'}</dt><dd>{detail.total}</dd></div>
                    <div><dt>Generated</dt><dd>{detail.generated}</dd></div>
                    <div><dt>Skipped</dt><dd>{detail.skipped}</dd></div>
                    <div><dt>Failed</dt><dd style={detail.errors > 0 ? { color: 'var(--red)', fontWeight: 600 } : undefined}>{detail.errors}</dd></div>
                    <div><dt>SMS sent</dt><dd>{detail.smsSent}</dd></div>
                    <div><dt>Alert</dt><dd>{ALERT_LABELS[detail.alertSent] || 'None needed'}</dd></div>
                    <div><dt>Finished</dt><dd>{detail.finishedAt ? fmtDateTime(detail.finishedAt) : <span className="muted">Not recorded</span>}{detail.durationMs != null && <div className="ops-cell-sub">{fmtDuration(detail.durationMs)}</div>}</dd></div>
                  </dl>
                  {detail.status === 'running' && <div className="ops-note">This run is still in progress. Refresh in a minute.</div>}
                  {detail.status === 'incomplete' && <div className="ops-note ops-note--review">No finish was recorded. The run likely timed out or crashed; check the {detail.trigger === 'batch' ? 'AgentFn' : 'NoteSchedulerFn'} logs in CloudWatch.</div>}
                  {detail.resultsTruncated && <div className="ops-note">Error messages in this run were shortened to fit the log.</div>}
                  <div className="ops-table-wrap">
                    <table className="ops-table">
                      <thead><tr><th>Client</th>{detail.trigger === 'batch' && <th>Week of</th>}<th>Caregiver</th><th>Outcome</th><th>Detail</th><th>SMS</th></tr></thead>
                      <tbody>
                        {(detail.results || []).length === 0 ? (
                          <tr><td colSpan={6}><span className="muted">{detail.status === 'running' || detail.status === 'incomplete' ? 'Results are recorded when the run finishes.' : 'No clients were processed.'}</span></td></tr>
                        ) : (detail.results || []).map((r, i) => (
                          <tr key={`${r.clientId || 'all'}-${i}`}>
                            <td className="ops-cell-main">{r.clientName || r.clientId || ''}</td>
                            {detail.trigger === 'batch' && <td>{fmtDate(r.weekBegin)}</td>}
                            <td>{r.caregiverName || r.caregiverId || ''}</td>
                            <td><StatusPill status={r.outcome} /></td>
                            <td>{resultDetail(r)}</td>
                            <td>{r.outcome === 'generated' ? (r.smsSent ? 'Yes' : 'No') : ''}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
            <div className="ops-drawer__foot">
              <a href={RERUN_URL} target="_blank" rel="noreferrer" className="ops-link">Re-run in the notes app</a>
              <span className="ops-actions">
                <button type="button" className="ops-btn" onClick={closeRun}>Close</button>
              </span>
            </div>
          </aside>
        </>
      )}
    </main>
  );
}
